/* eslint-disable no-unused-vars */
import { Plus } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  isWorkspaceAdminPage,
  isWorkspaceScopedPage,
  pageLookup
} from '../../config/pages';
import { useSession } from '../../contexts/SessionContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useActionDialog } from '../../hooks/useActionDialog';
import { ActionDialogRouter } from '../dialogs/ActionDialogRouter';
import { AccountSecurityDialog } from '../dialogs/AccountSecurityDialog';
import { AlertSourcesPage } from '../../pages/AlertSourcesPage';
import { AppReleasesPage } from '../../pages/AppReleasesPage';
import { ApplicationsPage } from '../../pages/ApplicationsPage';
import { AuditLogPage } from '../../pages/AuditLogPage';
import { BillingPage } from '../../pages/BillingPage';
import { ChannelsPage } from '../../pages/ChannelsPage';
import { EscalationPoliciesPage } from '../../pages/EscalationPoliciesPage';
import { EscalationRunsPage } from '../../pages/EscalationRunsPage';
import { OnCallSchedulesPage } from '../../pages/OnCallSchedulesPage';
import { PipelineRunsPage } from '../../pages/PipelineRunsPage';
import { ComingSoonPage } from '../../pages/ComingSoonPage';
import { ErrorBoundary } from '../ErrorBoundary';
import { DiagnosticsPage } from '../../pages/DiagnosticsPage';
import { DeliveryModelPage } from '../../pages/DeliveryModelPage';
import { ConnectionsPage } from '../../pages/ConnectionsPage';
import { GuidePage } from '../../pages/GuidePage';
import { DomainsPage } from '../../pages/DomainsPage';
import { EnvironmentsPage } from '../../pages/EnvironmentsPage';
import { MembersPage } from '../../pages/MembersPage';
import { OverviewPage } from '../../pages/OverviewPage';
import { PoliciesPage } from '../../pages/PoliciesPage';
import { PromotionsPage } from '../../pages/PromotionsPage';
import { ReleaseAccessPage } from '../../pages/ReleaseAccessPage';
import { ReleasesPage } from '../../pages/ReleasesPage';
import { RollbackPage } from '../../pages/RollbackPage';
import { RuntimeConfigPage } from '../../pages/RuntimeConfigPage';
import { SettingsPage } from '../../pages/SettingsPage';
import { AgentRunnerPanel } from '../AgentRunnerPanel';
import { AgentWorkPanel } from '../AgentWorkPanel';
import { ShipmentsPage } from '../../pages/ShipmentsPage';
import { SsoPage } from '../../pages/SsoPage';
import { StoragePolicyPage } from '../../pages/StoragePolicyPage';
import { Sidebar } from './Sidebar';
import { ProjectPageNav } from './ProjectPageNav';
import { Topbar } from './Topbar';
import { WorkspaceAdminNav } from './WorkspaceAdminNav';
import {
  normalizeAppProducts,
  primaryAppProduct,
  type AppProduct
} from '../../app-products';

type AppShellProps = {
  onNavigate: (path: string, opts?: { replace?: boolean }) => void;
};

function AppShell({ onNavigate }: AppShellProps) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [accountSecurityOpen, setAccountSecurityOpen] = useState(false);
  const { session, signOut } = useSession();
  const {
    workspaceLoading,
    activeOrgId,
    activeProjectId,
    activePage,
    activeOrganization,
    activeProject,
    activeEnvironment,
    activeReleaseAppVersion,
    releaseVersionOptions,
    visibleEnvironments,
    workspace,
    workspaceMenuOpen,
    userMenuOpen,
    setAppMenuOpen,
    setWorkspaceMenuOpen,
    setUserMenuOpen,
    setOrgMenuOpen,
    setEnvMenuOpen,
    selectOrganization,
    selectApp,
    selectNav,
    closeMenus,
    handleRefreshWorkspace
  } = useWorkspace();

  const actionDialogReturn = useActionDialog();
  const { openDialog } = actionDialogReturn;
  const closeMobileNav = useCallback(() => setMobileNavOpen(false), []);
  const toggleMobileNav = useCallback(
    () => setMobileNavOpen((open) => !open),
    []
  );
  const openAccountSecurity = useCallback(
    () => setAccountSecurityOpen(true),
    []
  );

  // Global mousedown handler to close menus when clicking outside
  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (
        target.closest('.appMenuWrap') ||
        target.closest('.orgMenuWrap') ||
        target.closest('.topbarOrgMenu') ||
        target.closest('.topbarBreadcrumbOrgBtn') ||
        target.closest('.topbarProjectMenu') ||
        target.closest('.topbarBreadcrumbProjectBtn') ||
        target.closest('.scopeSwitcherButton') ||
        target.closest('.scopeSwitcherPanel') ||
        target.closest('.topbarUserBtn') ||
        target.closest('.topbarUserMenu') ||
        target.closest('.sidebarFooter')
      ) {
        return;
      }
      closeMenus();
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [closeMenus]);

  // ── Dialog openers ────────────────────────────────────────────────────────

  const handleCreateOrganization = useCallback(() => {
    if (!session) return;
    openDialog({ kind: 'organization', name: 'Acme Labs' });
  }, [openDialog, session]);

  function handleCreateProject() {
    if (!session || !activeOrganization) return;
    const existingProducts = activeProject
      ? normalizeAppProducts(activeProject.products)
      : ['ota' as AppProduct];
    openDialog({
      kind: 'project',
      name: 'Acme Platform',
      appKind:
        primaryAppProduct(existingProducts) === 'notifications'
          ? 'notification'
          : primaryAppProduct(existingProducts) === 'workspace'
            ? 'product'
            : 'release',
      enabledProducts: existingProducts
    });
  }

  function handleCreateEnvironment() {
    if (!session || !activeOrganization || !activeProject) return;
    openDialog({
      kind: 'environment',
      name: 'Preview',
      slug: 'preview',
      region: activeOrganization.subtitle.toLowerCase().includes('eu')
        ? 'eu'
        : 'us'
    });
  }

  function handleCreateReleaseApp() {
    if (!session || !activeOrganization || !activeProject) return;
    openDialog({
      kind: 'release-app',
      organizationId: activeOrganization.id,
      projectId: activeProject.id,
      platform: 'ios',
      name: `${activeProject.name} iOS`,
      environment: ''
    });
  }

  function handlePromoteRelease() {
    if (!session) return;
    openDialog({
      kind: 'promotion',
      releaseArtifactId: '',
      releaseAppVersionId: '',
      environment: ''
    });
  }

  function handleCreateApiKey() {
    if (!session) return;
    openDialog({ kind: 'api-key', name: '', scopes: '*', expiresAt: '' });
  }

  function handleCreateInvite() {
    if (!session || !activeOrganization) return;
    openDialog({
      kind: 'invite',
      organizationId: activeOrganization.id,
      role: 'member',
      emailHint: ''
    });
  }

  function handleCreateRuntimeConfig() {
    if (!session || !activeOrganization || !activeProject) return;
    const defaultEnvironment: 'dev' | 'staging' | 'production' | 'internal' =
      activeEnvironment &&
      ['dev', 'staging', 'production', 'internal'].includes(
        activeEnvironment.slug
      )
        ? (activeEnvironment.slug as
            | 'dev'
            | 'staging'
            | 'production'
            | 'internal')
        : (visibleEnvironments[0]?.slug as typeof defaultEnvironment) || 'dev';
    const defaultReleaseAppVersion =
      activeReleaseAppVersion || releaseVersionOptions[0] || null;
    openDialog({
      kind: 'runtime-config',
      name: `${activeProject.name} ${defaultReleaseAppVersion?.version || activeEnvironment?.slug || 'dev'} runtime`,
      hostname: '',
      releaseAppVersionId: defaultReleaseAppVersion?.releaseAppVersionId || '',
      nativeAppId: '',
      platform: 'web',
      nativeVersionRange: '',
      environment: defaultEnvironment,
      expressApiHostname: '',
      staticCdnPrefix: '',
      analyticsEnabled: false,
      analyticsPageviewsEnabled: false,
      posthogApiKey: '',
      posthogHost: '',
      otelEnabled: false,
      otelCollectorUrl: '',
      priority: '100',
      isActive: true,
      values: []
    });
  }

  // ── Page action label ─────────────────────────────────────────────────────
  const pageActionLabel = useMemo(() => {
    if (activePage === 'applications') return 'New project';
    if (activePage === 'runtime-config') return 'New runtime rule';
    if (activePage === 'promotions') return 'Deploy update';
    if (activePage === 'release-access') return 'New key';
    if (activePage === 'members') return 'Invite member';
    return null;
  }, [activePage]);

  const activePageDefinition = pageLookup[activePage] ?? pageLookup['overview'];

  useEffect(() => {
    setMobileNavOpen(false);
  }, [activePage]);

  // ── Page router ───────────────────────────────────────────────────────────
  // Operational registries render inside the shared shell without owning scope.
  function renderPage() {
    if (activePage === 'overview') return <OverviewPage />;
    if (activePage === 'shipments') return <ShipmentsPage />;
    if (activePage === 'applications')
      return <ApplicationsPage openDialog={openDialog} />;
    if (activePage === 'environments') return <EnvironmentsPage />;
    if (activePage === 'domains') return <DomainsPage />;
    if (activePage === 'releases')
      return <ReleasesPage openDialog={openDialog} />;
    if (activePage === 'app-releases')
      return <AppReleasesPage openDialog={openDialog} />;
    if (activePage === 'channels')
      return <ChannelsPage openDialog={openDialog} />;
    if (activePage === 'promotions') return <PromotionsPage />;
    if (activePage === 'rollback')
      return <RollbackPage openDialog={openDialog} />;
    if (activePage === 'runtime-config')
      return <RuntimeConfigPage openDialog={openDialog} />;
    if (activePage === 'storage-policy') return <StoragePolicyPage />;
    if (activePage === 'release-access') return <ReleaseAccessPage />;
    if (activePage === 'diagnostics') return <DiagnosticsPage />;
    if (activePage === 'alert-sources')
      return <AlertSourcesPage openDialog={openDialog} />;
    if (activePage === 'escalation-runs') return <EscalationRunsPage />;
    if (activePage === 'oncall-schedules')
      return <OnCallSchedulesPage openDialog={openDialog} />;
    if (activePage === 'escalation-policies')
      return <EscalationPoliciesPage openDialog={openDialog} />;
    if (activePage === 'work-inbox') return <PipelineRunsPage mode="inbox" />;
    if (activePage === 'pipeline-runs')
      return <PipelineRunsPage mode="radar" />;
    if (activePage === 'delivery-model') return <DeliveryModelPage />;
    if (activePage === 'connections') return <ConnectionsPage />;
    if (activePage === 'agent-runners') return <AgentRunnerPanel />;
    if (activePage === 'agent-work') return <AgentWorkPanel />;
    if (activePage === 'guide') return <GuidePage />;
    if (activePage === 'settings')
      return <SettingsPage openDialog={openDialog} />;
    if (activePage === 'billing') return <BillingPage />;
    if (activePage === 'audit-log') return <AuditLogPage />;
    if (activePage === 'members') return <MembersPage />;
    if (activePage === 'sso') return <SsoPage />;
    if (activePage.startsWith('notifications-')) {
      return (
        <ComingSoonPage
          label="Notifications"
          title="Notifications"
          description="Multi-channel notification delivery for your apps."
          planned={[
            'Provider configuration (SMTP, webhooks, Slack)',
            'Template editor with variable substitution',
            'Workflow rules (trigger → channel → template)',
            'Delivery log with status and retry'
          ]}
        />
      );
    }
    return <PoliciesPage />;
  }

  return (
    <div className={`shell${mobileNavOpen ? ' mobileNavOpen' : ''}`}>
      {workspaceLoading ? (
        <div className="workspaceRefreshBar" aria-hidden="true" />
      ) : null}
      <Sidebar
        onClose={closeMobileNav}
        activePage={activePage}
        activeOrganization={activeOrganization}
        workJourneys={workspace.workJourneys}
        onSelectNav={selectNav}
      />
      <button
        type="button"
        className="mobileNavScrim"
        aria-label="Dismiss navigation"
        onClick={() => setMobileNavOpen(false)}
      />
      <div className="workspace">
        <Topbar
          onOpenOrganizationDialog={handleCreateOrganization}
          onOpenAccountSecurity={openAccountSecurity}
          onSignOut={signOut}
          mobileNavOpen={mobileNavOpen}
          onToggleMobileNav={toggleMobileNav}
          organizations={workspace.organizations}
          apps={workspace.apps}
          userDisplayName={session?.user.displayName}
          userEmail={session?.user.email}
          workspaceLoading={workspaceLoading}
          activeOrgId={activeOrgId}
          activeProjectId={activeProjectId}
          activeOrganization={activeOrganization}
          activeProject={activeProject}
          activePage={activePage}
          workspaceMenuOpen={workspaceMenuOpen}
          userMenuOpen={userMenuOpen}
          setAppMenuOpen={setAppMenuOpen}
          setWorkspaceMenuOpen={setWorkspaceMenuOpen}
          setUserMenuOpen={setUserMenuOpen}
          setOrgMenuOpen={setOrgMenuOpen}
          setEnvMenuOpen={setEnvMenuOpen}
          selectOrganization={selectOrganization}
          selectApp={selectApp}
          selectNav={selectNav}
        />
        <main
          className={`content content--${activePage}${['work-inbox', 'pipeline-runs', 'delivery-model', 'connections', 'agent-runners', 'agent-work'].includes(activePage) ? ' content--deliveryWorkspace' : ''}${activePage === 'guide' ? ' content--guideWorkspace' : ''}${isWorkspaceAdminPage(activePage) ? ' content--workspaceAdmin' : ''}`}
        >
          <section className="hero">
            <div className="heroCopy">
              <div className="heroKicker">
                {activePageDefinition?.group ?? ''}
              </div>
              <h1>{activePageDefinition?.label ?? 'Overview'}</h1>
              <p>{activePageDefinition?.description ?? ''}</p>
            </div>
            <div className="heroActions">
              {pageActionLabel ? (
                <button
                  type="button"
                  className="primaryButton"
                  onClick={() => {
                    if (activePage === 'applications') {
                      handleCreateProject();
                      return;
                    }
                    if (activePage === 'app-releases') {
                      handleCreateReleaseApp();
                      return;
                    }
                    if (activePage === 'environments') {
                      handleCreateEnvironment();
                      return;
                    }
                    if (activePage === 'runtime-config') {
                      handleCreateRuntimeConfig();
                      return;
                    }
                    if (activePage === 'promotions') {
                      handlePromoteRelease();
                      return;
                    }
                    if (activePage === 'release-access') {
                      handleCreateApiKey();
                      return;
                    }
                    if (activePage === 'members') {
                      handleCreateInvite();
                      return;
                    }
                    handleRefreshWorkspace();
                  }}
                >
                  <Plus size={16} />
                  <span>{pageActionLabel}</span>
                </button>
              ) : null}
            </div>
          </section>
          <div className="pageStage">
            {isWorkspaceAdminPage(activePage) ? <WorkspaceAdminNav /> : null}
            {!isWorkspaceScopedPage(activePage) ? <ProjectPageNav /> : null}
            <ErrorBoundary key={activePage} compact>
              {renderPage()}
            </ErrorBoundary>
          </div>
        </main>
      </div>
      <ActionDialogRouter
        actionDialogReturn={actionDialogReturn}
        onNavigate={onNavigate}
      />
      {accountSecurityOpen ? (
        <AccountSecurityDialog onClose={() => setAccountSecurityOpen(false)} />
      ) : null}
    </div>
  );
}

export { AppShell };
